import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Activity, ShieldAlert, ChevronDown, LogOut,
    Laptop, Smartphone, MonitorSmartphone, ShieldCheck,
} from "lucide-react";
import {
    fetchAccountActivity,
    logoutOtherSessions,
    fetchActiveSessions,
    revokeSession,
    fetchTrustedDevices,
    trustCurrentDevice,
    untrustDevice,
} from "@/lib/api";
import { readDeviceId } from "@/lib/deviceId";

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const now = Date.now();
        const diff = (now - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
        return d.toLocaleString(undefined, {
            month: "short", day: "numeric", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function detailText(item) {
    const d = item.details || {};
    const parts = [];
    if (d.from && d.to) parts.push(`${d.from} → ${d.to}`);
    else if (d.from) parts.push(d.from);
    else if (d.to) parts.push(d.to);
    if (d.back_to) parts.push(`restored to ${d.back_to}`);
    if (d.product_id) parts.push(d.product_id);
    if (d.tier) parts.push(`tier: ${d.tier}`);
    return parts.join(" · ");
}

function deviceLabel(ua) {
    if (!ua) return "Unknown device";
    const u = ua.toLowerCase();
    let os = "Unknown OS";
    if (u.includes("iphone")) os = "iPhone";
    else if (u.includes("ipad")) os = "iPad";
    else if (u.includes("android")) os = "Android";
    else if (u.includes("mac os x") || u.includes("macintosh")) os = "Mac";
    else if (u.includes("windows")) os = "Windows";
    else if (u.includes("linux")) os = "Linux";
    let browser = "Browser";
    if (u.includes("edg/")) browser = "Edge";
    else if (u.includes("chrome/")) browser = "Chrome";
    else if (u.includes("firefox/")) browser = "Firefox";
    else if (u.includes("safari/") && !u.includes("chrome/")) browser = "Safari";
    return `${browser} · ${os}`;
}

function DeviceIcon({ ua, className = "w-4 h-4" }) {
    if (!ua) return <MonitorSmartphone className={className} />;
    const u = ua.toLowerCase();
    if (u.includes("iphone") || u.includes("android") || u.includes("mobile")) {
        return <Smartphone className={className} />;
    }
    return <Laptop className={className} />;
}

function geoString(geo, ip) {
    if (!geo) return ip ? `IP ${ip}` : "";
    const place = [geo.city, geo.region, geo.country].filter(Boolean).join(", ");
    const isp = geo.isp ? ` · ${geo.isp}` : "";
    return `${place || "Unknown location"}${isp}${ip ? ` · IP ${ip}` : ""}`;
}

// User-facing "Recent account activity" card. Reads last 90 days of audit
// rows via /api/account/activity, friendly-labeled. Each row has a calm
// "Was this you?" prompt with a quick reset-password link.
export default function AccountActivitySection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [revokingId, setRevokingId] = useState(null);
    const [trustedDevices, setTrustedDevices] = useState([]);
    const [trustingNow, setTrustingNow] = useState(false);

    const loadTrustedDevices = useCallback(async () => {
        try {
            const r = await fetchTrustedDevices();
            setTrustedDevices(r.devices || []);
        } catch {
            /* graceful */
        }
    }, []);

    const trustedDeviceIds = new Set(trustedDevices.map((d) => d.device_id));
    const currentDeviceId = readDeviceId();
    const currentDeviceTrusted = currentDeviceId
        ? trustedDeviceIds.has(currentDeviceId)
        : false;

    const onTrustCurrentDevice = async () => {
        setTrustingNow(true);
        try {
            await trustCurrentDevice();
            toast.success("This device is trusted for 30 days.");
            await loadTrustedDevices();
        } catch (e) {
            toast.error(`Could not trust device: ${e.response?.data?.detail || e.message}`);
        } finally {
            setTrustingNow(false);
        }
    };

    const onUntrustDevice = async (device_id) => {
        if (!device_id) return;
        if (!window.confirm("Stop trusting this device? You'll start receiving the security email again on logins from here.")) return;
        try {
            await untrustDevice(device_id);
            toast.success("Device untrusted.");
            await loadTrustedDevices();
        } catch (e) {
            toast.error(`Could not untrust: ${e.response?.data?.detail || e.message}`);
        }
    };

    const loadSessions = useCallback(async () => {
        try {
            const r = await fetchActiveSessions();
            setSessions(r.sessions || []);
        } catch {
            /* graceful */
        } finally {
            setSessionsLoading(false);
        }
    }, []);

    const onRevokeOne = async (sid) => {
        if (!sid) return;
        if (!window.confirm("Sign out this device? It will need to enter the password again to access this account.")) return;
        setRevokingId(sid);
        try {
            await revokeSession(sid);
            toast.success("Device signed out.");
            await loadSessions();
        } catch (e) {
            toast.error(`Could not revoke: ${e.response?.data?.detail || e.message}`);
        } finally {
            setRevokingId(null);
        }
    };

    const onSignOutOthers = async () => {
        if (!window.confirm(
            "Sign out every other device and browser currently signed in to this account?\n\n"
            + "This won't sign you out here. You'll only need to re-enter your password "
            + "on those other devices."
        )) return;
        setRevoking(true);
        try {
            const r = await logoutOtherSessions();
            toast.success(
                r.revoked > 0
                    ? `Signed out ${r.revoked} other session${r.revoked === 1 ? "" : "s"}.`
                    : "No other active sessions to sign out.",
            );
            await loadSessions();
        } catch (e) {
            toast.error(`Could not sign out other sessions: ${e.response?.data?.detail || e.message}`);
        } finally {
            setRevoking(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const r = await fetchAccountActivity(50);
                setItems(r.items || []);
            } catch {
                /* gracefully degrade */
            } finally {
                setLoading(false);
            }
        })();
        loadSessions();
        loadTrustedDevices();
    }, [loadSessions, loadTrustedDevices]);

    const visible = showAll ? items : items.slice(0, 8);

    return (
        <section
            className="bl-card p-6 md:p-8 space-y-5"
            data-testid="account-activity-section"
            id="account-activity-section"
        >
            <div className="bl-eyebrow w-fit">
                <Activity className="w-3.5 h-3.5" />
                Recent activity
            </div>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <h2 className="font-display font-semibold text-2xl text-bl tracking-tight">
                        Activity on your account
                    </h2>
                    <p className="mt-2 text-sm text-bl-muted max-w-2xl leading-relaxed">
                        Last 90 days. If anything here doesn&apos;t look like you,{" "}
                        <Link to="/forgot-password" className="text-bl-primary hover:underline">
                            reset your password
                        </Link>{" "}
                        immediately.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onSignOutOthers}
                    disabled={revoking}
                    data-testid="activity-signout-others-btn"
                    className="bl-btn-secondary gap-2 shrink-0 disabled:opacity-60"
                >
                    <LogOut className="w-4 h-4" />
                    {revoking ? "Signing out…" : "Sign out other sessions"}
                </button>
            </div>

            {/* Active sessions list — per-device revoke */}
            <div className="space-y-3" data-testid="active-sessions-block">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-display font-semibold text-base text-bl">
                        Where you&apos;re signed in
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                        {!currentDeviceTrusted && currentDeviceId && (
                            <button
                                type="button"
                                onClick={onTrustCurrentDevice}
                                disabled={trustingNow}
                                className="inline-flex items-center gap-1.5 text-xs text-bl-primary hover:underline disabled:opacity-60"
                                data-testid="trust-this-device-btn"
                            >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {trustingNow ? "Trusting…" : "Trust this device for 30 days"}
                            </button>
                        )}
                        <span className="text-[11px] text-bl-muted">
                            {sessions.length} active
                        </span>
                    </div>
                </div>
                {sessionsLoading && (
                    <div className="text-sm text-bl-muted py-2" data-testid="sessions-loading">
                        Loading sessions…
                    </div>
                )}
                {!sessionsLoading && sessions.length === 0 && (
                    <div className="text-sm text-bl-muted py-2" data-testid="sessions-empty">
                        No active sessions tracked yet.
                    </div>
                )}
                {!sessionsLoading && sessions.length > 0 && (
                    <ul
                        className="divide-y divide-bl border border-bl rounded-xl overflow-hidden"
                        data-testid="sessions-list"
                    >
                        {sessions.map((s, idx) => {
                            const isTrusted = s.device_id && trustedDeviceIds.has(s.device_id);
                            return (
                            <li
                                key={s.session_id || idx}
                                className="px-4 sm:px-5 py-3 bg-white"
                                data-testid={`session-row-${idx}`}
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1 flex items-start gap-3">
                                        <DeviceIcon
                                            ua={s.user_agent}
                                            className="w-4 h-4 text-bl-primary mt-0.5 shrink-0"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-bl truncate">
                                                    {deviceLabel(s.user_agent)}
                                                </span>
                                                {s.current && (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-bl-primary/10 text-bl-primary"
                                                        data-testid={`session-current-badge-${idx}`}
                                                    >
                                                        This device
                                                    </span>
                                                )}
                                                {isTrusted && (
                                                    <span
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                        data-testid={`session-trusted-badge-${idx}`}
                                                    >
                                                        <ShieldCheck className="w-3 h-3" />
                                                        Trusted
                                                    </span>
                                                )}
                                            </div>
                                            {(s.ip || s.geo) && (
                                                <div className="mt-0.5 text-[11px] text-bl-muted font-mono-bl truncate">
                                                    {geoString(s.geo, s.ip)}
                                                </div>
                                            )}
                                            <div className="mt-0.5 text-[10px] text-bl-muted">
                                                Started {formatWhen(s.created_at)}
                                                {s.last_seen_at
                                                    ? ` · last active ${formatWhen(s.last_seen_at)}`
                                                    : ""}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isTrusted && (
                                            <button
                                                type="button"
                                                onClick={() => onUntrustDevice(s.device_id)}
                                                className="text-[11px] text-bl-muted hover:text-bl underline"
                                                data-testid={`session-untrust-btn-${idx}`}
                                            >
                                                Untrust
                                            </button>
                                        )}
                                        {!s.current && (
                                            <button
                                                type="button"
                                                onClick={() => onRevokeOne(s.session_id)}
                                                disabled={revokingId === s.session_id}
                                                className="bl-btn-secondary text-xs gap-1.5 py-1.5 px-3 disabled:opacity-60"
                                                data-testid={`session-revoke-btn-${idx}`}
                                            >
                                                <LogOut className="w-3.5 h-3.5" />
                                                {revokingId === s.session_id ? "Revoking…" : "Revoke"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <h3 className="font-display font-semibold text-base text-bl pt-2">
                Recent events
            </h3>

            {loading && (
                <div className="text-sm text-bl-muted py-4" data-testid="activity-loading">
                    Loading…
                </div>
            )}

            {!loading && items.length === 0 && (
                <div className="text-sm text-bl-muted py-4" data-testid="activity-empty">
                    No recent account events yet.
                </div>
            )}

            {!loading && items.length > 0 && (
                <>
                    <ul className="divide-y divide-bl border border-bl rounded-xl overflow-hidden" data-testid="activity-list">
                        {visible.map((it, idx) => {
                            const detail = detailText(it);
                            const sensitive = it.action?.startsWith("account.email_") || it.action === "account.deletion_requested";
                            return (
                                <li
                                    key={`${it.action}-${it.created_at}-${idx}`}
                                    className="px-4 sm:px-5 py-3 bg-white"
                                    data-testid={`activity-row-${idx}`}
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                {sensitive && (
                                                    <ShieldAlert className="w-3.5 h-3.5 text-bl-primary shrink-0" />
                                                )}
                                                <span className="text-sm font-medium text-bl truncate">
                                                    {it.label}
                                                </span>
                                            </div>
                                            {detail && (
                                                <div className="mt-0.5 text-xs text-bl-muted font-mono-bl truncate">
                                                    {detail}
                                                </div>
                                            )}
                                            {it.ip && (
                                                <div className="mt-0.5 text-[10px] text-bl-muted font-mono-bl">
                                                    {it.geo
                                                        ? `${[it.geo.city, it.geo.region, it.geo.country].filter(Boolean).join(", ") || "Unknown"}${it.geo.isp ? ` · ${it.geo.isp}` : ""} · IP ${it.ip}`
                                                        : `IP ${it.ip}`}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-bl-muted whitespace-nowrap">
                                            {formatWhen(it.created_at)}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {items.length > 8 && (
                        <button
                            type="button"
                            onClick={() => setShowAll((s) => !s)}
                            className="inline-flex items-center gap-1.5 text-xs text-bl-primary hover:underline"
                            data-testid="activity-toggle-all"
                        >
                            <ChevronDown
                                className={`w-3.5 h-3.5 transition-transform ${showAll ? "rotate-180" : ""}`}
                            />
                            {showAll ? "Show fewer" : `Show all ${items.length} events`}
                        </button>
                    )}
                </>
            )}
        </section>
    );
}
